import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { CONTACT } from "@/data/content";
import { SUBURBS } from "@/data/suburbs";
import { useSiteContent } from "@/context/ContentContext";

export default function ServicesIndex() {
  const { categories } = useSiteContent();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title = "Tiling Services Perth | Bathrooms, Kitchens, Outdoor & Commercial | Perth Tiling WA";
  const description = "Explore Perth Tiling WA's complete tiling services — bathroom & wet areas, kitchen splashbacks, outdoor & pool, commercial fit-outs, feature walls and AS 3740 waterproofing. Free quotes across Perth.";
  const keywords = "tiling services Perth, tilers Perth, bathroom tiling, kitchen splashback, outdoor tiling, commercial tiling Perth, feature walls, waterproofing";
  const breadcrumb = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
  ];

  return (
    <SmoothScroll>
      <Seo pageKey="services" overrideTitle={title} overrideDescription={description} overrideKeywords={keywords} breadcrumb={breadcrumb} />
      <div className="grain-overlay" aria-hidden />
      <div data-testid="services-index-page" className="bg-[#f8fafc] min-h-screen">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-40 pb-20 md:pt-52 md:pb-28 border-b border-[#0f172a]/10">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748b]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#9c7a4d] transition-colors">Home</Link>
              <span>/</span>
              <span className="text-[#0f172a]">Services</span>
            </nav>
            <p className="overline !text-[#9c7a4d] mb-5">What We Do</p>
            <h1 className="font-serif font-light text-[#0f172a] text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              <Reveal load>Every aspect</Reveal>
              <Reveal load delay={0.12}>of tiling.</Reveal>
            </h1>
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-2xl text-[#475569] text-lg md:text-xl font-light leading-relaxed">
                From a single splashback to a full commercial fit-out, we install the full spectrum of tiling — ceramic, porcelain, mosaic, large-format and natural stone — with one uncompromising standard of craftsmanship.
              </p>
            </FadeUp>
            <FadeUp delay={0.28}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href="/#enquire" data-testid="services-index-enquire-cta" className="group inline-flex items-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#0f172a] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  Get a Free Quote <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center gap-3 border border-[#0f172a]/20 text-[#0f172a] hover:border-[#9c7a4d] hover:text-[#9c7a4d] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors">
                  <Phone className="h-4 w-4" /> {CONTACT.phone}
                </a>
              </div>
            </FadeUp>
          </div>
        </section>

        {/* Services grid */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {categories.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-6%" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link to={`/services/${c.id}`} data-testid={`services-index-card-${c.id}`} className="group relative overflow-hidden aspect-[16/10] border border-[#0f172a]/10 block">
                  <img src={c.image} alt={`${c.title} tiling in Perth`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover brightness-[0.55] group-hover:brightness-75 transition-[filter,transform] duration-700 group-hover:scale-105" />
                  <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-8">
                    <span className="text-[#c9a87c] text-xs uppercase tracking-[0.22em] mb-2">{c.index} — {c.tag}</span>
                    <h2 className="font-serif text-3xl md:text-4xl text-white flex items-center gap-3">
                      {c.title}
                      <ArrowRight className="h-5 w-5 text-[#c9a87c] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                    </h2>
                    <p className="mt-3 text-white/80 text-sm md:text-base font-light leading-relaxed max-w-xl">{c.body}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Areas cross-link */}
        <section className="py-16 md:py-20 bg-[#f1f5f9] border-y border-[#0f172a]/10" data-testid="services-index-areas">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
              <div>
                <p className="overline !text-[#9c7a4d] mb-4">Where We Work</p>
                <h2 className="font-serif font-light text-[#0f172a] text-2xl md:text-3xl leading-tight">
                  Servicing Perth's premier suburbs
                </h2>
              </div>
              <Link to="/areas" data-testid="services-index-all-areas-link" className="inline-flex items-center gap-2 text-[#64748b] hover:text-[#9c7a4d] text-sm uppercase tracking-[0.2em] transition-colors">
                View all areas <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-3">
              {SUBURBS.map((s) => (
                <Link key={s.slug} to={`/tiling/${s.slug}`} data-testid={`services-index-area-${s.slug}`} className="text-sm text-[#475569] hover:text-white hover:bg-[#9c7a4d] border border-[#0f172a]/15 hover:border-[#9c7a4d] px-4 py-2 transition-colors">
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
